import React from 'react'
import { Link } from 'expo-router'
import { Pressable, Text, View, useWindowDimensions } from 'react-native'
import { HomeHeader } from '../../../components/home/HomeHeader'
import { LogoEggProvider, useLogoEgg } from '../../../components/home/logoEgg/LogoEgg'
import { EnemySprite } from '../../../components/home/logoEgg/LogoEggSprites'
import { useTheme } from '../../../hooks/useTheme'
import { isShootingReaction, LOGO_ENEMIES, LOGO_REACTION_LABELS, LOGO_REACTIONS, LOGO_RIDICULOUS, LOGO_SEQUENCE } from '../../../lib/logoEasterEgg'

const GROUPS = [
  { title: 'Shots and combos', reactions: LOGO_REACTIONS.filter(isShootingReaction) },
  { title: 'Gags', reactions: LOGO_REACTIONS.filter(r => !isShootingReaction(r) && !LOGO_RIDICULOUS.includes(r)) },
  { title: 'Totally ridiculous', reactions: [...LOGO_RIDICULOUS] },
]
import { ENEMY_ART } from '../../../lib/logoEggArt'

function Chip({ label, onPress, testID }: { label: string; onPress: () => void; testID?: string }) {
  return <Pressable onPress={onPress} testID={testID} accessibilityRole="button"
    className="bg-paper border border-olive rounded-lg px-3 py-2">
    <Text className="font-typed text-ink text-xs">{label}</Text>
  </Pressable>
}

function Controls() {
  const egg = useLogoEgg()!
  const { theme, setTheme } = useTheme()
  return <View className="p-4 gap-3" style={{ maxWidth: 760, width: '100%', alignSelf: 'center' }}>
    <Text className="font-typed text-ink-soft text-xs">Tap the logo for the next step of the curated loop (the same {LOGO_SEQUENCE.length} in the same order for everyone, starting with the helmet salute; this page keeps its place in memory only), or pick one below. Shots line up somewhere new on every play, about half of them high (just under the header line) and the rest mid-screen. The page below is open water for the swim.</Text>
    <View className="flex-row flex-wrap gap-2">
      <Chip label="Next in loop" onPress={egg.tap} testID="logo-egg-next" />
      <Chip label="Reduced motion" onPress={() => egg.play('wink', { reducedMotion: true })} />
      <Chip label={theme === 'dark' ? 'Light theme' : 'Dark theme'} onPress={() => setTheme(theme === 'dark' ? 'light' : 'dark')} />
    </View>
    {GROUPS.map(group => <View key={group.title} className="gap-2">
      <Text className="font-stencil text-ink text-xs uppercase mt-1">{group.title}</Text>
      <View className="flex-row flex-wrap gap-2">
        {group.reactions.map(reaction => <Chip key={reaction} label={LOGO_REACTION_LABELS[reaction]} testID={`logo-egg-play-${reaction}`}
          onPress={() => egg.play(reaction, reaction === 'invasion' ? { enemies: ['puffer', 'jelly', 'crab'] } : {})} />)}
      </View>
    </View>)}
    <Text className="font-stencil text-ink text-xs uppercase mt-2">Fire at a character</Text>
    <View className="flex-row flex-wrap gap-3">
      {LOGO_ENEMIES.map(enemy => {
        const art = ENEMY_ART[enemy]
        return <Pressable key={enemy} onPress={() => egg.play(`fire-${enemy}`)} accessibilityRole="button" accessibilityLabel={`Fire at ${art.name}`}
          className="bg-paper border border-olive rounded-lg p-2 items-center gap-1" style={{ width: 92 }}>
          <EnemySprite enemy={enemy} width={art.grid[0]!.length * 3} height={art.grid.length * 3} />
          <Text className="font-typed text-ink text-[11px]">{art.name}</Text>
        </Pressable>
      })}
    </View>
    <Link href="/preview/logo-easter-egg-frames" asChild>
      <Pressable><Text className="font-typed text-blood text-xs underline">Film strips of every reaction (static frames) →</Text></Pressable>
    </Link>
  </View>
}

/** Dev preview: the real home header with the easter egg, plus buttons for every reaction. */
export default function LogoEasterEggPreview() {
  const { width } = useWindowDimensions()
  return <View className="flex-1 bg-canvas" testID="logo-egg-preview">
    <LogoEggProvider>
      <HomeHeader displayName="yakuza" desktop={width >= 720} maxWidth={width >= 720 ? 1100 : undefined} onMaps={() => {}} onBots={() => {}} onHallOfFame={() => {}} onSettings={() => {}} />
      <Controls />
    </LogoEggProvider>
  </View>
}
